/**
 * A small formatter for the messages: `{name}`, `{n, number}` and `{n, plural, one {# file} other {# files}}`
 * (with `=0` exact cases and `#` for the number), nested to any depth. It is the part of ICU MessageFormat
 * the interface needs, and it throws on a malformed message so that `npm test` finds it (web/test/i18n.test.ts).
 */
export type Params = Readonly<Record<string, string | number>>;

interface Parsed {
  readonly text: string;
  readonly end: number;
}

function skipSpaces(source: string, from: number): number {
  let i = from;
  while (i < source.length && /\s/u.test(source.charAt(i))) i += 1;
  return i;
}

/** Reads `{ ... }` starting at the `{` and returns the inside. */
function readBraces(source: string, from: number): Parsed {
  let depth = 0;
  for (let i = from; i < source.length; i += 1) {
    const ch = source.charAt(i);
    if (ch === '{') depth += 1;
    else if (ch === '}') {
      depth -= 1;
      if (depth === 0) return { text: source.slice(from + 1, i), end: i + 1 };
    }
  }
  throw new Error(`Unclosed brace in message: ${source}`);
}

function plural(locale: string, argument: string, body: string, params: Params): string {
  const value = params[argument];
  if (typeof value !== 'number') throw new Error(`Plural argument "${argument}" must be a number`);
  const cases = new Map<string, string>();
  let i = 0;
  while (i < body.length) {
    i = skipSpaces(body, i);
    if (i >= body.length) break;
    let j = i;
    while (j < body.length && !/[\s{]/u.test(body.charAt(j))) j += 1;
    const selector = body.slice(i, j);
    j = skipSpaces(body, j);
    if (body.charAt(j) !== '{') throw new Error(`Plural case "${selector}" needs braces`);
    const inner = readBraces(body, j);
    cases.set(selector, inner.text);
    i = inner.end;
  }
  if (!cases.has('other')) throw new Error(`Plural "${argument}" has no "other" case`);
  const category = new Intl.PluralRules(locale).select(value);
  const chosen = cases.get(`=${String(value)}`) ?? cases.get(category) ?? cases.get('other');
  if (chosen === undefined) throw new Error(`Plural "${argument}" has no "other" case`);
  const number = new Intl.NumberFormat(locale).format(value);
  return format(locale, chosen, params, number);
}

/** Formats `message` for `locale` (an `Intl` tag). `hash` replaces `#` inside a plural case. */
export function format(locale: string, message: string, params: Params = {}, hash: string | null = null): string {
  let out = '';
  let i = 0;
  while (i < message.length) {
    const ch = message.charAt(i);
    if (ch === "'" && message.charAt(i + 1) === "'") {
      out += "'";
      i += 2;
    } else if (ch === "'" && (message.charAt(i + 1) === '{' || message.charAt(i + 1) === '}')) {
      // '{' … '}' quoted: the braces are text
      const close = message.indexOf("'", i + 2);
      if (close === -1) throw new Error(`Unclosed quote in message: ${message}`);
      out += message.slice(i + 1, close);
      i = close + 1;
    } else if (ch === '#' && hash !== null) {
      out += hash;
      i += 1;
    } else if (ch === '{') {
      const inner = readBraces(message, i);
      const parts = splitArgument(inner.text);
      const name = parts.name;
      if (parts.kind === null) {
        const value = params[name];
        if (value === undefined) throw new Error(`Missing argument "${name}" in message: ${message}`);
        out += String(value);
      } else if (parts.kind === 'number') {
        const value = params[name];
        if (typeof value !== 'number') throw new Error(`Argument "${name}" must be a number`);
        out += new Intl.NumberFormat(locale).format(value);
      } else if (parts.kind === 'plural') {
        out += plural(locale, name, parts.rest, params);
      } else {
        throw new Error(`Unknown argument type "${parts.kind}" in message: ${message}`);
      }
      i = inner.end;
    } else if (ch === '}') {
      throw new Error(`Unmatched brace in message: ${message}`);
    } else {
      out += ch;
      i += 1;
    }
  }
  return out;
}

function splitArgument(inner: string): { readonly name: string; readonly kind: string | null; readonly rest: string } {
  const first = inner.indexOf(',');
  if (first === -1) return { name: inner.trim(), kind: null, rest: '' };
  const second = inner.indexOf(',', first + 1);
  if (second === -1) return { name: inner.slice(0, first).trim(), kind: inner.slice(first + 1).trim(), rest: '' };
  return { name: inner.slice(0, first).trim(), kind: inner.slice(first + 1, second).trim(), rest: inner.slice(second + 1) };
}

/** The names of the arguments a message uses (placeholders and plural selectors), for the catalogue check. */
export function argumentsOf(message: string): readonly string[] {
  const names = new Set<string>();
  const walk = (text: string): void => {
    let i = 0;
    while (i < text.length) {
      if (text.charAt(i) === "'" && text.charAt(i + 1) === "'") {
        i += 2;
      } else if (text.charAt(i) === "'" && (text.charAt(i + 1) === '{' || text.charAt(i + 1) === '}')) {
        const close = text.indexOf("'", i + 2);
        i = close === -1 ? text.length : close + 1;
      } else if (text.charAt(i) === '{') {
        const inner = readBraces(text, i);
        const parts = splitArgument(inner.text);
        names.add(parts.name);
        if (parts.kind === 'plural') {
          let k = 0;
          while (k < parts.rest.length) {
            k = skipSpaces(parts.rest, k);
            if (k >= parts.rest.length) break;
            let j = k;
            while (j < parts.rest.length && !/[\s{]/u.test(parts.rest.charAt(j))) j += 1;
            j = skipSpaces(parts.rest, j);
            const body = readBraces(parts.rest, j);
            walk(body.text);
            k = body.end;
          }
        }
        i = inner.end;
      } else {
        i += 1;
      }
    }
  };
  walk(message);
  return [...names].sort();
}

/** Every plural in a message with the selectors it spells out (`one`, `other`, `=0`…), for the catalogue check. */
export function pluralsOf(message: string): readonly { readonly argument: string; readonly selectors: readonly string[] }[] {
  const found: { argument: string; selectors: string[] }[] = [];
  const walk = (text: string): void => {
    let i = 0;
    while (i < text.length) {
      if (text.charAt(i) === "'" && text.charAt(i + 1) === "'") {
        i += 2;
      } else if (text.charAt(i) === "'" && (text.charAt(i + 1) === '{' || text.charAt(i + 1) === '}')) {
        const close = text.indexOf("'", i + 2);
        i = close === -1 ? text.length : close + 1;
      } else if (text.charAt(i) === '{') {
        const inner = readBraces(text, i);
        const parts = splitArgument(inner.text);
        if (parts.kind === 'plural') {
          const entry = { argument: parts.name, selectors: [] as string[] };
          found.push(entry);
          let k = 0;
          while (k < parts.rest.length) {
            k = skipSpaces(parts.rest, k);
            if (k >= parts.rest.length) break;
            let j = k;
            while (j < parts.rest.length && !/[\s{]/u.test(parts.rest.charAt(j))) j += 1;
            entry.selectors.push(parts.rest.slice(k, j));
            j = skipSpaces(parts.rest, j);
            const body = readBraces(parts.rest, j);
            walk(body.text);
            k = body.end;
          }
        }
        i = inner.end;
      } else {
        i += 1;
      }
    }
  };
  walk(message);
  return found;
}

/** The tags (`<link>…</link>`) a message uses, for the catalogue check. */
export function tagsOf(message: string): readonly string[] {
  return [...message.matchAll(/<(\w+)>[\s\S]*?<\/\1>/gu)].map((match) => match[1] ?? '').sort();
}
